import {can} from '../lib/permissions';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { calendarMonthCells, calendarEventOnDay, romeDate } from "../lib/businessValidation";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmtDate } from "../lib/format";
import { displayInstant } from "../lib/rentalTime";
import VehicleCalendar from "../components/VehicleCalendar";
import VehicleColor from "../components/VehicleColor";
import useIsPhone from "../hooks/useIsPhone";
import { ArrowDown, ArrowUp, CalendarDays, ChevronLeft, ChevronRight, FileText, Wrench } from "lucide-react";

const TYPE_STYLE = {
  assegnazione: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  manutenzione: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  scadenza: "bg-red-500/20 text-red-300 border-red-500/40",
};
const DETAIL_FIELDS = [["vehicle_plate", "Veicolo"], ["assignee_name", "Assegnatario"], ["status", "Stato"], ["type", "Tipo"], ["category", "Categoria"], ["start_date", "Ritiro"], ["start_at", "Ritiro con orario (Europe/Rome)"], ["end_at", "Rientro previsto con orario (Europe/Rome)"], ["end_actual_at", "Rientro effettivo con orario (Europe/Rome)"], ["end_date_planned", "Rientro previsto"], ["end_date_actual", "Rientro effettivo"], ["planned_date", "Data programmata"], ["opened_date", "Data apertura"], ["closed_date", "Data chiusura"], ["expiry_date", "Scadenza"], ["number", "Numero"], ["description", "Descrizione"], ["notes", "Note"]];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
// Phone month: dots in the grid, the touched day's events listed below.
const TYPE_DOT = { assegnazione: "bg-indigo-400", manutenzione: "bg-amber-400", scadenza: "bg-red-400" };
const TYPE_LABEL = { assegnazione: "Noleggio", manutenzione: "Manutenzione", scadenza: "Scadenza documenti" };
// The days between pickup and return: the same colour, lighter, so the days with something to do stand out.
const DURING_STYLE = "border-dashed bg-indigo-500/5 text-indigo-300 border-indigo-500/30";
const hourOf = (iso) => { const at = iso ? new Date(iso) : null; return at && !Number.isNaN(at.getTime())
  ? new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" }).format(at) : ""; };

/** What a rental is on a day: its pickup, its return, both, or a day in between. */
export function rentalMoment(event, day) {
  if (event.type !== "assegnazione") return null;
  const pickup = day === event.date, back = !!event.end && day === event.end;
  const at = (word, iso) => [word, hourOf(iso)].filter(Boolean).join(" ");
  if (pickup && back) return { kind: "both", label: `${at("Ritiro", event.start_at)} · ${at("Rientro", event.end_at)}` };
  if (pickup) return { kind: "pickup", label: at("Ritiro", event.start_at) };
  if (back) return { kind: "return", label: at("Rientro", event.end_at) };
  return { kind: "during", label: "In corso" };
}
const isDuring = (event, day) => rentalMoment(event, day)?.kind === "during";
// Pickups and returns first, the days in between last.
const forDay = (events, day) => [...events].sort((a, b) => isDuring(a, day) - isDuring(b, day));
// A full day (more than this) is shown grouped: what to do first, the rest after.
export const FULL_DAY = 3;
const GROUPS = [["pickup", "Ritiri", "ritiro", "ritiri"], ["return", "Rientri", "rientro", "rientri"],
  ["other", "Manutenzioni e scadenze", "manutenzione o scadenza", "manutenzioni e scadenze"], ["during", "In corso", "in corso", "in corso"]];
/** The day's events in groups, in this order; empty groups are left out. */
export function dayGroups(events, day) {
  const groups = Object.fromEntries(GROUPS.map(([key]) => [key, []]));
  for (const event of events) {
    const kind = rentalMoment(event, day)?.kind;
    groups[!kind ? "other" : kind === "both" ? "pickup" : kind]?.push(event);
  }
  return GROUPS.filter(([key]) => groups[key].length).map(([key, title, one, many]) => ({ key, title, events: groups[key],
    count: `${groups[key].length} ${groups[key].length === 1 ? one : many}` }));
}
export const displayTitle = (event) => (event.title || "").replace(/^Assegnazione\b/, "Noleggio");
const shortDate = (date) => date ? new Date(`${date}T00:00:00Z`).toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "UTC" }) : "";

// In a month cell the colour already says the kind: "Assegnazione GB456DE - Anna
// Bianchi" becomes the plate on one line and the customer on the next.
export function cellLabel(event) {
  const text = event.title || "";
  if (event.type === "assegnazione") {
    const [plate, ...who] = text.replace(/^Assegnazione\s+/, "").split(" - ");
    return [plate, who.join(" - ")];
  }
  return [text, ""];
}

function eventWhen(event) {
  if (event.type !== "assegnazione") return TYPE_LABEL[event.type];
  const period = event.end && event.end !== event.date ? `dal ${shortDate(event.date)} al ${shortDate(event.end)}`
    : event.end ? shortDate(event.date) : `dal ${shortDate(event.date)}`;
  return event.awaiting_return ? `${period} · Rientro da verificare` : period;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const canViewEvent = type => can(user,`${type === "assegnazione" ? "rentals" : type === "manutenzione" ? "maintenances" : "deadlines"}.read`);
  const [view, setView] = useState("month");
  const phone = useIsPhone();
  const [phoneDay, setPhoneDay] = useState(() => romeDate());
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => new Date(`${romeDate()}T00:00:00Z`));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const dayTrigger = useRef(null);
  const detailRequest = useRef(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const openEvent = async (event, trigger) => {
    if (trigger && !selectedDay) dayTrigger.current = trigger;
    const request = ++detailRequest.current;
    setSelectedEvent(event); setDetail(null); setDetailError("");
    try { const { data } = await api.get(`/calendar/events/${event.type}/${event.id}`); if (request === detailRequest.current) setDetail(data); }
    catch (error) { if (request === detailRequest.current) setDetailError(apiError(error)); }
  };
  const closeDialog = () => { detailRequest.current++; setSelectedEvent(null); setSelectedDay(null); };

  const load = async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/calendar/events"); setEvents(data); }
    catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { year, month } = { year: cursor.getUTCFullYear(), month: cursor.getUTCMonth() };
  const cells = useMemo(() => calendarMonthCells(year, month), [year, month]);
  const today = romeDate();

  const eventsFor = (date) => date ? events.filter((e) => calendarEventOnDay(e, date)) : [];
  // Changing month on a phone selects today when it is in that month, otherwise the 1st.
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  useEffect(() => { setPhoneDay(today.startsWith(monthKey) ? today : `${monthKey}-01`); }, [monthKey, today]);


  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-tile h-11 w-11" aria-hidden="true"><CalendarDays className="h-6 w-6" /></div>
          <div><h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Calendario</h1><p className="text-sm text-muted-foreground">Assegnazioni, manutenzioni e scadenze</p></div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" aria-label="Mese precedente" onClick={() => setCursor(new Date(Date.UTC(year, month - 1, 1)))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[160px] text-center font-heading text-lg font-semibold capitalize">{cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric", timeZone: "UTC" })}</span>
          <Button variant="outline" size="icon" aria-label="Mese successivo" onClick={() => setCursor(new Date(Date.UTC(year, month + 1, 1)))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(`${romeDate()}T00:00:00Z`))}>Oggi</Button>
        </div>
      </div>

      <div className="flex gap-2" role="group" aria-label="Vista calendario"><Button variant={view === "month" ? "default" : "outline"} aria-pressed={view === "month"} onClick={() => setView("month")}>Calendario mensile</Button><Button variant={view === "vehicles" ? "default" : "outline"} aria-pressed={view === "vehicles"} onClick={() => setView("vehicles")}>Per veicolo</Button></div>
      {view === "vehicles" ? <VehicleCalendar days={cells.filter(Boolean)} /> : <>
      <div className="flex flex-wrap gap-3 text-xs">
        {phone ? <>
          <span className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded border ${TYPE_STYLE.assegnazione}`} />Noleggio: ritiro e rientro</span>
          <span className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded border ${DURING_STYLE}`} />Noleggio in corso</span>
          {["manutenzione", "scadenza"].map((k) => (<span key={k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded border ${TYPE_STYLE[k]}`} />{TYPE_LABEL[k]}</span>))}
        </> : <>
          {Object.entries(LINE_KIND).map(([key, { icon: Icon, tone, label }]) => <span key={key} className="flex items-center gap-1.5"><Icon className={`h-3.5 w-3.5 ${tone}`} aria-hidden="true" />{label}</span>)}
          <span className="flex items-center gap-1.5 text-muted-foreground">«N in corso»: auto fuori, niente da fare quel giorno</span>
        </>}
      </div>

      {loading ? <p role="status" className="py-8 text-center text-muted-foreground">Caricamento calendario…</p> : error ? (
        <Card className="border-border bg-card p-5" role="alert"><p>{error}</p><Button className="mt-3" variant="outline" onClick={load}>Riprova</Button></Card>
      ) : phone ? <PhoneMonth cells={cells} today={today} day={phoneDay} onDay={setPhoneDay} eventsFor={eventsFor} canViewEvent={canViewEvent} openEvent={openEvent} />
      : <Card className="border-border bg-card p-3">
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => <div key={w} className="py-2 text-center text-xs font-medium text-muted-foreground">{w}</div>)}
          {cells.map((date, i) => {
            const evs = eventsFor(date);
            const isToday = date === today;
            return (
              <div key={i} data-testid={date ? `cal-day-${date}` : undefined} className={`min-h-[92px] rounded-lg border p-1.5 ${date ? "border-border bg-secondary/20" : "border-transparent"} ${isToday ? "ring-1 ring-primary" : ""}`}>
                {date && <MonthCell date={date} events={evs} isToday={isToday} canViewEvent={canViewEvent} openEvent={openEvent}
                  openDay={(trigger) => { dayTrigger.current = trigger; setSelectedDay(date); }} />}
              </div>
            );
          })}
        </div>
      </Card>}
      </>}
      <Dialog open={!!selectedDay || !!selectedEvent} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" onCloseAutoFocus={(event) => { event.preventDefault(); dayTrigger.current?.focus(); }}>
          {selectedEvent ? <>
            <DialogHeader><DialogTitle>{displayTitle(selectedEvent)}</DialogTitle><DialogDescription>Dettaglio del singolo evento · sola lettura</DialogDescription></DialogHeader>
            {selectedDay && <Button variant="outline" onClick={() => { detailRequest.current++; setSelectedEvent(null); }}>Torna agli eventi del giorno</Button>}
            {detailError ? <div role="alert"><p>{detailError}</p><Button onClick={() => openEvent(selectedEvent)}>Riprova</Button></div> : !detail ? <p role="status">Caricamento dettaglio…</p> : <dl data-testid="cal-event-detail" data-record-id={detail.id} className="grid gap-3 sm:grid-cols-2">
              {DETAIL_FIELDS.filter(([key]) => detail[key] !== undefined && detail[key] !== null && detail[key] !== "").map(([key, label]) => <div key={key} className="min-w-0"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="break-words text-sm">{key.endsWith("_at") ? displayInstant(detail[key]) : key.includes("date") ? fmtDate(detail[key]) : ["status", "type", "category"].includes(key) ? String(detail[key]).replaceAll("_", " ") : String(detail[key])}</dd></div>)}
            </dl>}
          </> : <>
          <DialogHeader><DialogTitle>Eventi del {fmtDate(selectedDay)}</DialogTitle><DialogDescription>Tutti i {eventsFor(selectedDay).length} eventi del giorno.</DialogDescription></DialogHeader>
          <div data-testid="cal-day-events"><DayList events={eventsFor(selectedDay)} day={selectedDay} canViewEvent={canViewEvent} openEvent={(event) => openEvent(event)} /></div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhoneMonth({ cells, today, day, onDay, eventsFor, canViewEvent, openEvent }) {
  const dayEvents = eventsFor(day);
  return <>
    <Card className="border-border bg-card p-2" data-testid="cal-phone-month">
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => <div key={w} className="py-1 text-center text-xs font-medium text-muted-foreground">{w.slice(0, 1)}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={i} />;
          const evs = eventsFor(date), selected = date === day, isToday = date === today;
          return <button key={date} type="button" onClick={() => onDay(date)} aria-pressed={selected} data-testid={`cal-day-${date}`}
            aria-label={`${fmtDate(date)}: ${evs.length ? `${evs.length} ${evs.length === 1 ? "evento" : "eventi"}` : "niente in programma"}`}
            className={`flex h-12 flex-col items-center rounded-lg border pt-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "border-primary bg-primary/15" : "border-border bg-secondary/20"} ${isToday ? "font-bold text-primary" : ""}`}>
            {Number(date.slice(8, 10))}
            <span className="mt-1 flex items-center gap-0.5" aria-hidden="true">
              {forDay(evs, date).slice(0, 3).map((e) => <span key={e.id + e.type} className={`h-1.5 w-1.5 rounded-full ${isDuring(e, date) ? "border border-indigo-400" : TYPE_DOT[e.type]}`} />)}
              {evs.length > 3 && <span className="text-[10px] leading-none text-muted-foreground">+</span>}
            </span>
          </button>;
        })}
      </div>
    </Card>
    <section aria-live="polite" className="space-y-2" data-testid="cal-phone-day">
      <h2 className="font-heading text-base font-semibold capitalize">{new Date(`${day}T00:00:00Z`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}</h2>
      <DayList events={dayEvents} day={day} canViewEvent={canViewEvent} openEvent={openEvent} />
    </section>
  </>;
}

/** One event of a day, the same on the phone and in the day window: what to do, which car, when. */
function DayEventRow({ event, day, canOpen, onOpen }) {
  const Tag = canOpen ? "button" : "div", moment = rentalMoment(event, day), during = moment?.kind === "during";
  return <Tag {...(canOpen ? { type: "button", onClick: onOpen } : {})} data-testid={`cal-event-${event.id}`}
    className="flex min-h-14 w-full items-stretch gap-3 rounded-lg border border-border bg-card p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <span className={`w-1 shrink-0 rounded-full ${during ? "border border-dashed border-indigo-400" : TYPE_DOT[event.type]}`} aria-hidden="true" />
    <span className="min-w-0 flex-1">
      {moment && !during && <span className="block text-xs font-bold uppercase tracking-wide text-indigo-300">{moment.label}</span>}
      <span className="flex items-center gap-2 text-sm font-medium"><VehicleColor vehicleId={event.vehicle_id} size="sm" /><span className="truncate">{displayTitle(event)}</span></span>
      <span className="block text-xs text-muted-foreground">{during ? `In corso · ${eventWhen(event)}` : eventWhen(event)}</span>
    </span>
    {canOpen && <ChevronRight className="h-4 w-4 shrink-0 self-center text-muted-foreground" aria-hidden="true" />}
  </Tag>;
}

/** A day's events: a plain list, or for a full day groups with "In corso" closed (nothing to do there). */
function DayList({ events, day, canViewEvent, openEvent }) {
  if (!events.length) return <p className="text-sm text-muted-foreground">Niente in programma.</p>;
  const row = (e) => <DayEventRow key={e.id + e.type} event={e} day={day} canOpen={canViewEvent(e.type)} onOpen={(click) => openEvent(e, click.currentTarget)} />;
  if (events.length <= FULL_DAY) return <div className="space-y-2">{forDay(events, day).map(row)}</div>;
  return <div className="space-y-4" data-testid="cal-day-groups">{dayGroups(events, day).map((group) => group.key === "during" ? (
    <details key={group.key} className="group space-y-2" data-testid="cal-group-during">
      <summary className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-semibold">{group.title} ({group.events.length})<span className="text-xs font-normal text-muted-foreground group-open:hidden">· tocca per vedere</span></summary>
      <div className="space-y-2">{group.events.map(row)}</div>
    </details>
  ) : <section key={group.key} className="space-y-2" data-testid={`cal-group-${group.key}`}>
      <h3 className="text-sm font-semibold">{group.title} ({group.events.length})</h3>
      {group.events.map(row)}
    </section>)}</div>;
}

// Computer month: every cell the same — short lines for what to do (pickups, returns,
// workshop, documents), then how many cars are simply out that day.
const LINE_KIND = {
  pickup: { icon: ArrowUp, tone: "text-indigo-300", label: "Ritiro" },
  return: { icon: ArrowDown, tone: "text-indigo-300", label: "Rientro" },
  manutenzione: { icon: Wrench, tone: "text-amber-300", label: "Manutenzione" },
  scadenza: { icon: FileText, tone: "text-red-300", label: "Scadenza documenti" },
};
const LINES = 3;

export function cellLines(events, day) {
  const lines = [];
  for (const event of events) {
    const moment = rentalMoment(event, day);
    if (moment?.kind === "pickup" || moment?.kind === "both") lines.push({ event, kind: "pickup", at: hourOf(event.start_at) });
    if (moment?.kind === "return" || moment?.kind === "both") lines.push({ event, kind: "return", at: hourOf(event.end_at) });
    if (!moment) lines.push({ event, kind: event.type, at: "" });
  }
  const order = { pickup: 0, return: 1, manutenzione: 2, scadenza: 3 };
  lines.sort((a, b) => order[a.kind] - order[b.kind] || (a.at || "99").localeCompare(b.at || "99"));
  return { lines, during: events.filter((e) => isDuring(e, day)).length };
}

function MonthCell({ date, events, isToday, canViewEvent, openEvent, openDay }) {
  const { lines, during } = cellLines(events, date);
  const hidden = lines.length - LINES;
  const footer = [hidden > 0 && `+${hidden} ${hidden === 1 ? "altro" : "altri"}`, during > 0 && `${during} in corso`].filter(Boolean).join(" · ");
  return <>
    <button type="button" disabled={!events.length} onClick={(click) => openDay(click.currentTarget)} aria-label={`${fmtDate(date)}: ${events.length} eventi. Mostra l'elenco`}
      className={`mb-1 block rounded px-0.5 text-xs font-medium enabled:hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isToday ? "text-primary" : "text-muted-foreground"}`}>{Number(date.slice(8, 10))}</button>
    <div className="space-y-0.5">
      {lines.slice(0, LINES).map(({ event, kind, at }) => {
        const { icon: Icon, tone } = LINE_KIND[kind], canOpen = canViewEvent(event.type), Tag = canOpen ? "button" : "div";
        const [plate, customer] = cellLabel(event);
        return <Tag key={event.id + event.type + kind} {...(canOpen ? { type: "button", onClick: (click) => openEvent(event, click.currentTarget) } : {})} data-testid={`cal-event-${event.id}`}
          title={[displayTitle(event), customer && `Cliente: ${customer}`, at && `${LINE_KIND[kind].label} ${at}`].filter(Boolean).join(" · ")}
          className="flex w-full min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] leading-tight hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Icon className={`h-3 w-3 shrink-0 ${tone}`} aria-label={LINE_KIND[kind].label} />
          {at && <span className="shrink-0 tabular-nums text-muted-foreground">{at}</span>}
          {event.type === "assegnazione" && <VehicleColor vehicleId={event.vehicle_id} size="sm" className="shrink-0 [&>span:first-child]:h-2.5 [&>span:first-child]:w-2.5 [&>span:first-child]:border" />}
          <span className="truncate font-mono font-semibold">{event.type === "assegnazione" ? plate : plate.replace(/^Manutenzione\s+/, "")}</span>
        </Tag>;
      })}
      {footer && <button type="button" onClick={(click) => openDay(click.currentTarget)} data-testid={`cal-more-${date}`}
        className="block w-full truncate rounded px-1 py-0.5 text-left text-[11px] text-muted-foreground hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{footer}</button>}
    </div>
  </>;
}
